import dayjs from "dayjs";
import { GoogleCalButton } from "../../components/Appointments";
import { Fragment, useState } from "react";
import { db, eq } from "../../lib/api";
import { invalidate, useQuery } from "../../lib/hooks";
import { AREA_LABEL, LEAD_AREAS, useRefData, type Area } from "../../lib/refdata";
import { useSession } from "../../lib/session";
import { PageHead } from "../../components/Shell";
import { CalendarView } from "../../components/Calendar";
import { Empty, ErrorBox, Field, Loading, Panel, Seg, Select, Sheet, fmtDate, money, useToast } from "../../components/ui";
import { openPerson } from "../../components/PersonDrawer";

/** Google Calendar is connected: booked events go there by themselves. */
const useGoogleOn = () => !!useRefData().data?.setting("google")?.connected;
const syncGoogle = () => db.rpc("google_sync_now").catch(() => undefined);

export default function Events() {
  const { can } = useSession();
  const googleOn = useGoogleOn();
  const [editing, setEditing] = useState<any | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const events = useQuery("events:all", () => db.select("events", { select: "*,event_people(person_id,people(full_name))", order: "event_date.desc.nullslast" }));
  const [view, setView] = useState<"list" | "calendar">(() => { try { return (localStorage.getItem("rca.events.view") as any) || "list"; } catch { return "list"; } });
  const [month, setMonth] = useState(() => dayjs().startOf("month"));
  const pickView = (v: "list" | "calendar") => { setView(v); try { localStorage.setItem("rca.events.view", v); } catch { /* fine */ } };
  return (
    <>
      <PageHead help="events" title="Events" sub="Every event you find, book or attend, and who you met there."
        actions={can("events", "edit") && <button className="btn primary" onClick={() => setEditing({ status: "Booked" })}>Add event</button>} />
      <div className="filters"><Seg value={view} onChange={pickView} options={[{ value: "list", label: "List" }, { value: "calendar", label: "Calendar" }]} /></div>
      <ErrorBox error={events.error} />
      {view === "calendar" ? <Panel><div className="panel-body"><CalendarView month={month} onMonth={setMonth}
        items={(events.data ?? []).filter((e: any) => e.event_date).map((e: any) => ({ id: e.id, at: `${e.event_date}T${e.start_time ?? "00:00"}`, allDay: !e.start_time,
          title: e.name ?? "Event", sub: [e.status, e.location].filter(Boolean).join(" · "), tone: e.status === "Attended" ? "gold" : e.status === "Booked" ? "default" : "muted" }))}
        onOpen={(id) => setEditing((events.data ?? []).find((e: any) => e.id === id))} /></div></Panel> :
      <Panel>
        {events.loading && !events.data ? <Loading /> : !events.data?.length ? <Empty title="No events yet">Add the next networking event you plan to attend.</Empty> : (
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th>Event</th><th>Date</th><th>Status</th><th>Host</th><th className="num">People met</th><th className="num">Cost</th></tr></thead>
              <tbody>
                {events.data.map((e: any) => (
                  <Fragment key={e.id}>
                    <tr onClick={() => setOpen(open === e.id ? null : e.id)}>
                      <td><strong>{e.name}</strong><div className="small muted">{e.location}</div></td>
                      <td className="nowrap">{fmtDate(e.event_date)}{e.start_time && <div className="small muted">{fmtTime(e.start_time)}{e.end_time ? ` – ${fmtTime(e.end_time)}` : ""}</div>}</td>
                      <td><span className="pill">{e.status}</span></td>
                      <td>{e.host}</td>
                      <td className="num">{e.event_people.length}</td>
                      <td className="num">{money(e.cost)}</td>
                    </tr>
                    {open === e.id && (
                      <tr><td colSpan={6} style={{ background: "var(--paper)" }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {e.event_date && (googleOn ? <GoogleStatus e={e} /> : <GoogleCalButton small {...eventCal(e)} />)}
                          {e.event_people.map((p: any) => <PersonChip key={p.person_id} e={e} p={p} editable={can("events", "edit")} />)}
                          {can("events", "edit") && <button className="btn small ghost" onClick={() => setEditing(e)}>Edit event</button>}
                          {can("events", "edit") && <EventFix e={e} onDone={() => { setOpen(null); invalidate("events"); invalidate("people"); }} />}
                        </div>
                      </td></tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>}
      {editing && <EventSheet ev={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

/** Move everyone met at an event to another list, or delete the event (each person keeps "Met at: …"). */
function EventFix({ e, onDone }: { e: any; onDone: () => void }) {
  const toast = useToast();
  const [mode, setMode] = useState<"" | "move" | "delete">("");
  const [off, setOff] = useState(true);
  const [err, setErr] = useState("");
  const n = e.event_people.length;
  async function move(to: Area) {
    setErr("");
    try {
      const r = await db.rpc<{ moved: number; off_event: number }>("event_move_people", { p_event: e.id, p_to: to, p_off_event: off });
      toast(`${r.moved} moved to ${AREA_LABEL[to]}${r.off_event ? ` and taken off ${e.name}` : ""}`); setMode(""); onDone();
    }
    catch (x) { setErr((x as Error).message); }
  }
  async function del() {
    setErr("");
    try { const r = await db.rpc<{ ok: boolean; noted: number }>("event_delete", { p_event: e.id }); toast(`Event deleted${r.noted ? `; "Met at: ${e.name}" kept on ${r.noted} people` : ""}`); onDone(); }
    catch (x) { setErr((x as Error).message); }
  }
  return <>
    {n > 0 && mode !== "move" && <button className="btn small ghost" onClick={() => setMode("move")}>Move everyone to…</button>}
    {mode === "move" && <span className="toolbar" style={{ margin: 0 }}>
      <span className="small muted">Move {n} {n === 1 ? "person" : "people"} to</span>
      {LEAD_AREAS.filter((a) => a !== "networking").map((a) => <button key={a} className="btn small" onClick={() => move(a)}>{AREA_LABEL[a]}</button>)}
      <button className="btn small ghost" onClick={() => setMode("")}>Cancel</button>
      <label className="small" style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
        <input type="checkbox" checked={off} onChange={(x) => setOff(x.target.checked)} /> Also take them off this event (added by mistake)
      </label>
    </span>}
    {mode !== "delete" ? <button className="btn small ghost danger" onClick={() => setMode("delete")}>Delete event</button> : <span className="toolbar" style={{ margin: 0 }}>
      <span className="small">Delete “{e.name}”? The people stay{n ? ` and each gets a note “Met at: ${e.name}”` : ""}.</span>
      <button className="btn small danger-fill" onClick={del}>Delete</button>
      <button className="btn small ghost" onClick={() => setMode("")}>Keep</button>
    </span>}
    {err && <span className="small" style={{ color: "var(--alert)" }}>{err}</span>}
  </>;
}

/** One person met at an event: open them, or take them off it. */
function PersonChip({ e, p, editable }: { e: any; p: any; editable: boolean }) {
  const toast = useToast();
  async function off() {
    try { await db.rpc("event_remove_person", { p_event: e.id, p_person: p.person_id }); invalidate("events"); invalidate(`person:${p.person_id}`); toast(`${p.people?.full_name} taken off ${e.name}`); }
    catch (x) { toast((x as Error).message); }
  }
  return <span className="btn-pair" style={{ display: "inline-flex" }}>
    <button className="btn small" onClick={() => openPerson(p.person_id)}>{p.people?.full_name}</button>
    {editable && <button className="btn small ghost" title={`Not from ${e.name}: take them off this event`} aria-label={`Take ${p.people?.full_name} off this event`} onClick={off}>×</button>}
  </span>;
}

/** Where this event stands with Google Calendar. */
function GoogleStatus({ e }: { e: any }) {
  if (!e.google_event_id && e.event_date < dayjs().subtract(1, "day").format("YYYY-MM-DD")) return null;
  if (!["Booked", "Attended"].includes(e.status)) return <span className="small muted">Not on Google Calendar until it's Booked</span>;
  if (e.google_error) return <span className="small" style={{ color: "var(--alert)" }}>Google Calendar: {e.google_error}</span>;
  return <span className="pill warm" title="Booked events are copied to your Google Calendar automatically, so Calendly sees the time as busy">{e.google_event_id ? "On Google Calendar ✓" : "Going to Google Calendar…"}</span>;
}

const eventCal = (e: any) => ({
  title: e.name ?? "Event", location: e.location, details: [e.host && `Host: ${e.host}`, e.notes].filter(Boolean).join("\n"),
  allDay: !e.start_time,
  start: e.start_time ? dayjs(`${e.event_date}T${e.start_time.slice(0, 5)}`) : dayjs(e.event_date),
  end: e.start_time ? (e.end_time ? dayjs(`${e.event_date}T${e.end_time.slice(0, 5)}`) : dayjs(`${e.event_date}T${e.start_time.slice(0, 5)}`).add(2, "hour")) : null,
});
const fmtTime = (t: string) => dayjs(`2000-01-01T${t}`).format("h:mm A");

function EventSheet({ ev, onClose }: { ev: any; onClose: () => void }) {
  const ref = useRefData();
  const toast = useToast();
  const [f, setF] = useState<any>(ev);
  const [error, setError] = useState<Error | null>(null);
  const googleOn = useGoogleOn();
  const set = (k: string) => (v: string) => setF((x: any) => ({ ...x, [k]: v || null }));
  async function save() {
    try {
      if (f.start_time && f.end_time && f.end_time <= f.start_time) throw new Error("The end time must be after the start time.");
      const body = { name: f.name, industry: f.industry, host: f.host, event_date: f.event_date, start_time: f.start_time || null, end_time: f.end_time || null, location: f.location, status: f.status, cost: f.cost ? Number(f.cost) : 0, notes: f.notes };
      if (ev.id) await db.update("events", { id: eq(ev.id) }, body); else await db.insert("events", body);
      if (googleOn) syncGoogle().then(() => invalidate("events"));
      invalidate("events"); toast(googleOn && ["Booked", "Attended"].includes(f.status) ? "Event saved · on your Google Calendar" : "Event saved"); onClose();
    } catch (e) { setError(e as Error); }
  }
  return (
    <Sheet title={ev.id ? "Edit event" : "Add event"} onClose={onClose} footer={<>{f.event_date && !googleOn && <GoogleCalButton {...eventCal(f)} />}{googleOn && <span className="small muted" style={{ marginRight: "auto" }}>{["Booked", "Attended"].includes(f.status) ? "Goes to your Google Calendar when you save" : "Set the status to Booked to put it on your Google Calendar"}</span>}<button className="btn primary" onClick={save}>Save event</button></>}>
      <ErrorBox error={error} />
      <div className="form-grid">
        <Field label="Event name" wide><input className="input" value={f.name ?? ""} onChange={(e) => set("name")(e.target.value)} /></Field>
        <Field label="Date"><input type="date" className="input" value={f.event_date ?? ""} onChange={(e) => set("event_date")(e.target.value)} /></Field>
        <Field label="Starts"><input type="time" className="input" value={(f.start_time ?? "").slice(0, 5)} onChange={(e) => set("start_time")(e.target.value)} /></Field>
        <Field label="Ends"><input type="time" className="input" value={(f.end_time ?? "").slice(0, 5)} onChange={(e) => set("end_time")(e.target.value)} /></Field>
        <Field label="Status"><Select value={f.status} onChange={set("status")} options={ref.data?.list("EventStatus") ?? []} /></Field>
        <Field label="Location"><input className="input" value={f.location ?? ""} onChange={(e) => set("location")(e.target.value)} /></Field>
        <Field label="Host / association"><input className="input" value={f.host ?? ""} onChange={(e) => set("host")(e.target.value)} /></Field>
        <Field label="Industry"><input className="input" value={f.industry ?? ""} onChange={(e) => set("industry")(e.target.value)} /></Field>
        <Field label="Cost ($)"><input type="number" className="input" value={f.cost ?? ""} onChange={(e) => set("cost")(e.target.value)} /></Field>
        <Field label="Notes" wide><textarea className="textarea" value={f.notes ?? ""} onChange={(e) => set("notes")(e.target.value)} /></Field>
      </div>
    </Sheet>
  );
}
